import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PlayerAPI } from '@elfsight/widget-media-video';
import { useOnInView } from 'react-intersection-observer';
import {
  useVideoAutoplay,
  VISIBILITY_THRESHOLD
} from '../../../video-autoplay-provider';

interface UseVideoIntersectionParams {
  videoId: string;
  index: number;
  isInPopup: boolean;
  videoRef: RefObject<PlayerAPI>;
  canUseAutoplay: boolean;
  isHovered: boolean;
}

export function useVideoIntersection({
  videoId,
  index,
  isInPopup,
  videoRef,
  canUseAutoplay,
  isHovered
}: UseVideoIntersectionParams) {
  const {
    register,
    unregister,
    onVisibilityChange,
    onHover,
    onHoverEnd,
    recomputePlaying
  } = useVideoAutoplay();
  const [hasBeenInView, setHasBeenInView] = useState(false);
  const [isPlayerMounted, setIsPlayerMounted] = useState(false);
  const lastVisibilityRef = useRef<number>(0);
  const isVisibleRef = useRef(false);

  const onViewRef = useOnInView(
    (_inView, entry) => {
      const { intersectionRatio, isIntersecting } = entry;

      if (intersectionRatio >= VISIBILITY_THRESHOLD) {
        // Монтируем плеер при достижении порога видимости
        if (!hasBeenInView) setHasBeenInView(true);
        setIsPlayerMounted(true);
      } else if (!isIntersecting) {
        // Размонтируем плеер только при полном уходе из viewport,
        // освобождая сетевые соединения браузера.
        // В диапазоне 0 < ratio < VISIBILITY_THRESHOLD стейт не меняем
        // чтобы не размонтировать частично видимый плеер.
        setIsPlayerMounted(false);
      }

      lastVisibilityRef.current = intersectionRatio;
      isVisibleRef.current = intersectionRatio >= VISIBILITY_THRESHOLD;

      // Отслеживание изменения видимости
      onVisibilityChange(videoId, intersectionRatio);

      // Регистрация/отмена регистрации видео при изменении видимости
      if (intersectionRatio >= VISIBILITY_THRESHOLD) {
        register({
          id: videoId,
          index,
          isInPopup,
          wasCalculated: false,
          visibility: intersectionRatio,
          ref: videoRef.current
        });
      } else {
        unregister(videoId);
      }
    },
    { threshold: [0, VISIBILITY_THRESHOLD] }
  );

  const videoIntersectionRef = useCallback(
    (node: Element | null | undefined) => {
      if (node) {
        onViewRef(node);
      }
    },
    [onViewRef]
  );

  // Вызывается когда плеер готов к воспроизведению.
  // Если видео уже во viewport (ref был null при первой регистрации),
  // повторно регистрируем с актуальным ref и пересчитываем воспроизведение.
  const onPlayerReady = useCallback(() => {
    if (!isVisibleRef.current || !videoRef.current) return;

    register({
      id: videoId,
      index,
      isInPopup,
      wasCalculated: false,
      visibility: lastVisibilityRef.current,
      ref: videoRef.current
    });
    recomputePlaying();
  }, [videoId, index, isInPopup, videoRef, register, recomputePlaying]);

  // Отслеживание hover для autoplay
  useEffect(() => {
    if (!canUseAutoplay || isInPopup) return;

    if (isHovered) {
      onHover(videoId);
    } else {
      onHoverEnd(videoId);
    }
  }, [isHovered, videoId, canUseAutoplay, isInPopup, onHover, onHoverEnd]);

  return {
    videoIntersectionRef,
    hasBeenInView,
    isPlayerMounted,
    onPlayerReady
  };
}
